import { Input } from "@/components/_Input";
import { useSaveTruckImplementMutation } from "@/store";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Stack, Tooltip } from "@mantine/core";
import { ContextModalProps } from "@mantine/modals";
import { SubmitHandler, useForm } from "react-hook-form";
import z from "zod";

type Schema = z.infer<typeof schema>;
const schema = z.object({
  id: z.number().optional(),
  name: z
    .string({ error: "Nome do implemento é obrigatório" })
    .trim()
    .regex(/^[a-zA-Z0-9\s]+$/, "Não são permitidos caracteres especiais")
    .min(2, "Mínimo de 2 caracteres")
    .max(100, "Máximo de 100 caracteres"),
  shaftQuantity: z.preprocess(
    (value) => {
      const input = typeof value === "string" ? value : String(value);
      return parseInt(input, 10);
    },
    z.number({ error: "Deve ser um número" }).int().positive("Deve ser um número positivo").min(1, "Mínimo de 1 eixo").max(20, "Máximo de 20 eixos")
  ),
  grossCapacity: z.string().min(1, "Mínimo de 1 caractere").max(20, "Máximo de 20 caracteres"),
  netCapacity: z.string().min(1, "Mínimo de 1 caractere").max(20, "Máximo de 20 caracteres")
});

type CreateEditTruckImplementationModalProps = ContextModalProps<Schema>;
export function CreateEditTruckImplementationModal({ id, context, innerProps }: CreateEditTruckImplementationModalProps) {
  const [triggerSave, { isLoading: isSaving }] = useSaveTruckImplementMutation();
  const { id: innerId } = innerProps;

  const {
    handleSubmit,
    reset,
    control,
    formState: { isSubmitting, isDirty, disabled }
  } = useForm({
    mode: "all",
    resolver: zodResolver(schema),
    defaultValues: innerProps,
    disabled: isSaving
  });

  const onSubmit: SubmitHandler<Schema> = async (data) => {
    await triggerSave(data);
    handleDeny();
  };

  const handleDeny = () => {
    context.closeModal(id);
    reset();
  };

  return (
    <Stack>
      <Input.Text label="Implemento" placeholder="Digite o implemento do caminhão" control={control} name="name" autoFocus withAsterisk />
      <Input.Text
        label="Quantidade de Eixos"
        formatValue={(input) => {
          const cleanValue = input.slice(0, 2).replace(/\D/g, "");
          return cleanValue;
        }}
        placeholder="Digite a quantidade de eixos do implemento"
        inputMode="numeric"
        control={control}
        name="shaftQuantity"
        withAsterisk
      />
      <Input.Text
        label="Capacidade Bruta"
        placeholder="Digite a capacidade bruta do implemento"
        control={control}
        maskProps={{ mask: "99.999" }}
        name="grossCapacity"
        withAsterisk
      />
      <Input.Text
        label="Capacidade Líquida"
        placeholder="Digite a capacidade líquida do implemento"
        control={control}
        maskProps={{ mask: "99.999" }}
        name="netCapacity"
        withAsterisk
      />

      <div className="grid grid-cols-1 gap-[inherit] md:grid-cols-2">
        <Button variant="default" onClick={handleDeny} disabled={isSubmitting || disabled}>
          Cancelar
        </Button>

        <Tooltip label="Nenhuma alteração detectada!" color="red" disabled={isDirty}>
          <Button onClick={handleSubmit(onSubmit)} loading={isSubmitting} disabled={!isDirty || disabled} color={innerId ? "yellow" : "green"}>
            {innerId ? "Salvar Alterações" : "Salvar"}
          </Button>
        </Tooltip>
      </div>
    </Stack>
  );
}
